/**
 * Licence Card Component
 * Displays a pilot licence with type, rating, expiry date, and expiry warning.
 *
 * @param {Object} licence - { id, licence_type, rating_category, expiry_date }
 * @param {Object} [options]
 * @param {Function} [options.onEdit] - (licence) => void
 * @param {Function} [options.onDelete] - (licence) => void
 * @returns {HTMLElement}
 */

import { formatDateUK } from '../utils/format.js';

/**
 * Check whether a licence is expired relative to a reference date.
 * @param {{ expiry_date: string }} licence
 * @param {Date} [referenceDate] - defaults to now
 * @returns {boolean}
 */
export function isLicenceExpired(licence, referenceDate) {
  if (!licence || !licence.expiry_date) return false;
  const ref = referenceDate || new Date();
  const expiry = new Date(licence.expiry_date);
  expiry.setHours(23, 59, 59, 999);
  return expiry < ref;
}

/**
 * Check whether a licence expires within the next 30 days.
 * @param {{ expiry_date: string }} licence
 * @param {Date} [referenceDate]
 * @returns {boolean}
 */
export function isLicenceExpiringSoon(licence, referenceDate) {
  if (!licence || !licence.expiry_date) return false;
  const ref = referenceDate || new Date();
  if (isLicenceExpired(licence, ref)) return false;
  const expiry = new Date(licence.expiry_date);
  const thirtyDaysFromNow = new Date(ref);
  thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
  return expiry <= thirtyDaysFromNow;
}

/**
 * Create a licence card DOM element.
 */
export function createLicenceCard(licence, options = {}) {
  const { onEdit, onDelete } = options;
  const card = document.createElement('div');
  card.className = 'licence-card';

  const expired = isLicenceExpired(licence);
  const expiringSoon = isLicenceExpiringSoon(licence);

  if (expired) card.classList.add('licence-card--expired');
  else if (expiringSoon) card.classList.add('licence-card--warning');

  const statusIcon = expired ? '🔴' : expiringSoon ? '🟡' : '🟢';
  const statusText = expired ? 'Expired' : expiringSoon ? 'Expiring soon' : 'Valid';

  card.innerHTML = `
    <div class="licence-card__header">
      <span class="licence-card__type">${licence.licence_type || '—'}</span>
      <span class="licence-card__status" title="${statusText}">${statusIcon}</span>
    </div>
    <div class="licence-card__body">
      <div class="licence-card__rating">${licence.rating_category || '—'}</div>
      <div class="licence-card__expiry">Expires: ${licence.expiry_date ? formatDateUK(licence.expiry_date) : '—'}</div>
    </div>
    <div class="licence-card__actions"></div>
  `;

  const actionsEl = card.querySelector('.licence-card__actions');

  if (onEdit) {
    const editBtn = document.createElement('button');
    editBtn.className = 'btn btn--small';
    editBtn.textContent = 'Edit';
    editBtn.addEventListener('click', () => onEdit(licence));
    actionsEl.appendChild(editBtn);
  }

  if (onDelete) {
    const deleteBtn = document.createElement('button');
    deleteBtn.className = 'btn btn--small btn--danger';
    deleteBtn.textContent = 'Delete';
    deleteBtn.addEventListener('click', () => onDelete(licence));
    actionsEl.appendChild(deleteBtn);
  }

  return card;
}
